import { useState } from 'react'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { FormItem, Form } from '@/components/ui/Form'
import { apiForgotPassword } from '@/services/AuthService'
import CaptchaField from '@/components/form/fields/CaptchaField'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { ZodType } from 'zod'
import type { CommonProps } from '@/@types/common'

interface ForgotPasswordFormProps extends CommonProps {
    emailSent: boolean
    setEmailSent?: (complete: boolean) => void
    setMessage?: (message: string) => void
}

type ForgotPasswordFormSchema = {
    email: string
    recaptcha?: string | null
}

const validationSchema: ZodType<ForgotPasswordFormSchema> = z.object({
    email: z.string().email().min(5),
    recaptcha: z.string().optional().nullable(),
})

const getErrorMessage = (errors: unknown) => {
    if (typeof errors === 'string') {
        return errors
    }

    if (
        errors &&
        typeof errors === 'object' &&
        'response' in errors &&
        errors.response &&
        typeof errors.response === 'object' &&
        'data' in errors.response &&
        errors.response.data &&
        typeof errors.response.data === 'object' &&
        'message' in errors.response.data &&
        typeof errors.response.data.message === 'string'
    ) {
        return errors.response.data.message
    }

    return 'Failed to request password reset'
}

const ForgotPasswordForm = (props: ForgotPasswordFormProps) => {
    const [isSubmitting, setSubmitting] = useState<boolean>(false)
    const [captchaKey, setCaptchaKey] = useState(0)

    const { className, setMessage, setEmailSent, emailSent, children } = props

    const {
        handleSubmit,
        formState: { errors },
        control,
        resetField,
    } = useForm<ForgotPasswordFormSchema>({
        resolver: zodResolver(validationSchema),
    })

    const resetCaptcha = () => {
        resetField('recaptcha')
        setCaptchaKey((key) => key + 1)
    }

    const onForgotPassword = async (values: ForgotPasswordFormSchema) => {
        const { email } = values

        setSubmitting(true)

        try {
            const resp = await apiForgotPassword<{ message: string }>({
                email,
                recaptcha: values.recaptcha,
            })
            if (resp) {
                setSubmitting(false)
                setEmailSent?.(true)
            }
        } catch (errors) {
            setMessage?.(getErrorMessage(errors))
            resetCaptcha()
            setSubmitting(false)
        }

        setSubmitting(false)
    }

    return (
        <div className={className}>
            {!emailSent ? (
                <Form onSubmit={handleSubmit(onForgotPassword, resetCaptcha)}>
                    <FormItem
                        label="Email"
                        invalid={Boolean(errors.email)}
                        errorMessage={errors.email?.message}
                    >
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="email"
                                    placeholder="Email"
                                    autoComplete="off"
                                    {...field}
                                />
                            )}
                        />
                    </FormItem>
                    <CaptchaField
                        key={captchaKey}
                        name="recaptcha"
                        control={control}
                        className="my-4"
                    />
                    <Button
                        block
                        loading={isSubmitting}
                        variant="solid"
                        type="submit"
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </Form>
            ) : (
                <>{children}</>
            )}
        </div>
    )
}

export default ForgotPasswordForm
